import { HttpClient, type HttpClientOptions } from './http';
import type {
  AuthResultDto,
  CompletePlaySessionRequest,
  CompleteGameplayMatchRequest,
  EmitGameEventResponse,
  GameAssetDto,
  GameOwnedAssetDto,
  PlaySessionDto,
  GameStoreCheckoutIntentDto,
  GameStoreDto,
  GameStoreOfferPurchaseResultDto,
  GameTrackingSessionDto,
  PagingResponse,
  RecordGameplayActionRequest,
  RefreshTokenRequest,
  SdkCreateGameStoreCheckoutIntentRequest,
  SdkPurchaseGameStoreOfferRuntimeRequest,
  StartPlaySessionRequest,
  TelegramLoginRequest,
} from './LooplaySDK.types';

export interface ServiceClientOptions extends HttpClientOptions {}

/**
 * Auth for the tracking endpoints (view/play-session/match/action), which accept
 * either a real user's `bearerToken` (full tracking, eligible for
 * quest/reward) or an `anonId` for an unauthenticated device
 * (analytics-only — the backend never awards quest/reward progress to an
 * anonymous id). `trackingSessionId`/`anonTrackingToken` come from
 * `bootstrapTrackingSession()` and should be attached either way, per the
 * platform's tracking contract.
 */
export interface TrackingAuth {
  bearerToken?: string;
  anonId?: string;
  trackingSessionId?: string;
  anonTrackingToken?: string;
}

/**
 * Auth for the creator-secret tier (`createGameStoreCheckoutIntent`/
 * `purchaseGameStoreOffer`) — moves real coin balance. `creatorKey`/
 * `gameSecret` identify your integration (never embed these in
 * client/browser code); `bearerToken` identifies the player you're
 * purchasing on behalf of, same as any other authenticated call.
 */
export interface SdkKeyPairAuth {
  creatorKey: string;
  gameSecret: string;
  bearerToken: string;
  trackingSessionId?: string;
}

/**
 * Low-level HTTP client that maps 1:1 to backend routes.
 * Methods requiring user auth accept `bearerToken` explicitly.
 */
export class ServiceClient {
  private readonly http: HttpClient;

  constructor(options: ServiceClientOptions) {
    this.http = new HttpClient(options);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Auth
  // ─────────────────────────────────────────────────────────────────────────────

  async telegramLogin(telegramInitData: string): Promise<AuthResultDto> {
    const body: TelegramLoginRequest = { telegramInitData };
    return this.http.request('POST', '/account/telegram-login', { body });
  }

  async refresh(refreshToken: string): Promise<AuthResultDto> {
    const body: RefreshTokenRequest = { refreshToken };
    return this.http.request('POST', '/account/refresh', { body });
  }

  async logout(refreshToken: string): Promise<boolean> {
    const body: RefreshTokenRequest = { refreshToken };
    return this.http.request('POST', '/account/logout', { body });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Games — one SDK integration = one game, resolved on every call from the
  // public `x-game-key` header (the game's `appId`). There is no multi-game
  // catalog browsing here (list/search/detail across games) and no `:id`/
  // `storeId` path segments — that's the app-facing surface, out of scope
  // for this per-game SDK. Never use the creator-secret tier
  // (`x-creator-key`/`x-game-secret`) from client/browser code — that would
  // leak the secret to every player.
  // ─────────────────────────────────────────────────────────────────────────────

  async recordGameView(gameKey: string, auth: TrackingAuth = {}): Promise<boolean> {
    // `referer` is a forbidden header name — browsers attach it automatically,
    // it cannot (and doesn't need to) be set here.
    await this.http.request('POST', '/sdk/games/views/record', {
      ...auth,
      headers: { 'x-game-key': gameKey, ...this.trackingHeaders(auth) },
    });
    return true;
  }

  /** Signs a fresh `playSessionToken` — required by `completePlaySession`/`completeGameplayMatch`/`recordGameplayAction`. */
  async startPlaySession(
    gameKey: string,
    auth: TrackingAuth = {},
    body: StartPlaySessionRequest = {}
  ): Promise<PlaySessionDto> {
    return this.http.request('POST', '/sdk/games/play-sessions/start', {
      ...auth,
      headers: { 'x-game-key': gameKey, ...this.trackingHeaders(auth) },
      body,
    });
  }

  async completePlaySession(
    gameKey: string,
    auth: TrackingAuth,
    body: CompletePlaySessionRequest
  ): Promise<boolean> {
    await this.http.request('POST', '/sdk/games/play-sessions/complete', {
      ...auth,
      headers: { 'x-game-key': gameKey, ...this.trackingHeaders(auth) },
      body,
    });
    return true;
  }

  async completeGameplayMatch(
    gameKey: string,
    auth: TrackingAuth,
    body: CompleteGameplayMatchRequest
  ): Promise<boolean> {
    await this.http.request('POST', '/sdk/games/matches/complete', {
      ...auth,
      headers: { 'x-game-key': gameKey, ...this.trackingHeaders(auth) },
      body,
    });
    return true;
  }

  async recordGameplayAction(
    gameKey: string,
    auth: TrackingAuth,
    body: RecordGameplayActionRequest
  ): Promise<EmitGameEventResponse> {
    return this.http.request('POST', '/sdk/games/actions/record', {
      ...auth,
      headers: { 'x-game-key': gameKey, ...this.trackingHeaders(auth) },
      body,
    });
  }

  /**
   * Anonymous runtimes must call this before any tracking call; logged-in
   * runtimes should still call it to get a `trackingSessionId` for unified
   * tracing — see `GameTrackingSessionDto`.
   */
  async bootstrapTrackingSession(anonId?: string): Promise<GameTrackingSessionDto> {
    return this.http.request('POST', '/sdk/games/tracking/bootstrap', {
      headers: { 'x-looplay-anon-id': anonId },
    });
  }

  /**
   * Lightweight integration heartbeat for review/liveops. This intentionally
   * does not record a view/play metric.
   */
  async detectGameIntegration(gameKey: string): Promise<boolean> {
    await this.http.request('POST', '/sdk/games/integration/detect', {
      headers: { 'x-game-key': gameKey },
    });
    return true;
  }

  private trackingHeaders(auth: TrackingAuth): Record<string, string | undefined> {
    return {
      'x-game-tracking-session-id': auth.trackingSessionId,
      'x-looplay-anon-token': auth.anonTrackingToken,
    };
  }

  /** Public storefront (sections + offers) for the integration's game. Read-only. */
  async getStore(gameKey: string): Promise<GameStoreDto> {
    return this.http.request('GET', '/sdk/games/store/current', {
      headers: { 'x-game-key': gameKey },
    });
  }

  /** Public catalog of purchasable/ownable assets for the game. Read-only. */
  async listGameAssets(gameKey: string): Promise<GameAssetDto[]> {
    return this.http.request('GET', '/sdk/games/store/assets', {
      headers: { 'x-game-key': gameKey },
    });
  }

  /** Assets the authenticated player already owns for this game. */
  async listMyGameAssets(bearerToken: string, gameKey: string): Promise<GameOwnedAssetDto[]> {
    return this.http.request('GET', '/sdk/games/store/assets/me', {
      bearerToken,
      headers: { 'x-game-key': gameKey },
    });
  }

  /** The authenticated player's past store purchases for this game. */
  async listMyStorePurchases(
    bearerToken: string,
    gameKey: string
  ): Promise<GameStoreOfferPurchaseResultDto[]> {
    return this.http.request('GET', '/sdk/games/store/purchases/me', {
      bearerToken,
      headers: { 'x-game-key': gameKey },
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Self-serve purchase (public tier) — same trust level as tracking: public
  // `x-game-key` + the purchasing player's own bearer token, no backend or
  // secret pair required. Safe to call directly from client/browser code —
  // `checkoutToken` (short-lived, server-signed) plays the same role as
  // `playSessionToken` on the tracking routes. `ApiClient.purchaseStoreOffer`
  // wraps this pair for you.
  // ─────────────────────────────────────────────────────────────────────────────

  /** Signs a `checkoutToken` for one purchase — required by `purchaseMyStoreOffer`. */
  async createMyStoreCheckoutIntent(
    gameKey: string,
    bearerToken: string,
    body: SdkCreateGameStoreCheckoutIntentRequest,
    trackingSessionId?: string
  ): Promise<GameStoreCheckoutIntentDto> {
    return this.http.request('POST', '/sdk/games/store/checkout-intent/me', {
      bearerToken,
      headers: { 'x-game-key': gameKey, 'x-game-tracking-session-id': trackingSessionId },
      body,
    });
  }

  async purchaseMyStoreOffer(
    gameKey: string,
    bearerToken: string,
    body: SdkPurchaseGameStoreOfferRuntimeRequest,
    trackingSessionId?: string
  ): Promise<GameStoreOfferPurchaseResultDto> {
    return this.http.request('POST', '/sdk/games/store/purchase/me', {
      bearerToken,
      headers: { 'x-game-key': gameKey, 'x-game-tracking-session-id': trackingSessionId },
      body,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Server-to-server (secret pair) — moves real coin balance. Requires the
  // creator+game *secret* pair (`x-creator-key`/`x-game-secret`), distinct
  // from the public `x-game-key` used everywhere above. Call these ONLY
  // from your own backend (e.g. a Node service holding the secret pair) —
  // never from client/browser code, or the secret is exposed to every
  // player. `ApiClient` deliberately does not expose these — it auto-injects
  // a *player's* access token for browser-facing calls and has no concept
  // of your creator secret. Prefer the self-serve tier above unless you
  // specifically need to initiate a purchase from your backend without the
  // player present.
  // ─────────────────────────────────────────────────────────────────────────────

  /** Signs a `checkoutToken` for one purchase — required by `purchaseGameStoreOffer`. */
  async createGameStoreCheckoutIntent(
    auth: SdkKeyPairAuth,
    body: SdkCreateGameStoreCheckoutIntentRequest
  ): Promise<GameStoreCheckoutIntentDto> {
    return this.http.request('POST', '/sdk/games/store/checkout-intent', {
      bearerToken: auth.bearerToken,
      headers: {
        'x-creator-key': auth.creatorKey,
        'x-game-secret': auth.gameSecret,
        'x-game-tracking-session-id': auth.trackingSessionId,
      },
      body,
    });
  }

  async purchaseGameStoreOffer(
    auth: SdkKeyPairAuth,
    body: SdkPurchaseGameStoreOfferRuntimeRequest
  ): Promise<GameStoreOfferPurchaseResultDto> {
    return this.http.request('POST', '/sdk/games/store/purchase', {
      bearerToken: auth.bearerToken,
      headers: {
        'x-creator-key': auth.creatorKey,
        'x-game-secret': auth.gameSecret,
        'x-game-tracking-session-id': auth.trackingSessionId,
      },
      body,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Balance
  // ─────────────────────────────────────────────────────────────────────────────

  async getBalances(bearerToken: string, query?: Record<string, any>): Promise<any> {
    return this.http.request('GET', '/sdk/balance', { bearerToken, query });
  }

  async getBalanceHistory(
    bearerToken: string,
    query?: Record<string, any>
  ): Promise<PagingResponse<any>> {
    return this.http.request('GET', '/sdk/balance/history', { bearerToken, query });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // User
  // ─────────────────────────────────────────────────────────────────────────────

  async getMyProfile(bearerToken: string): Promise<any> {
    return this.http.request('GET', '/sdk/user/profile', { bearerToken });
  }

  async getMyBalance(bearerToken: string): Promise<any> {
    return this.http.request('GET', '/sdk/user/balance', { bearerToken });
  }
}
